import { put, takeLatest } from 'redux-saga/effects';
import { AccountConfig } from 'api/account';
import { FieldSectionType } from 'api/field-sections/types';
import { Field, Task, TaskAPI } from 'api/tasks';
import { StoreActionProps } from 'data/core';
import { STATUS_FIELD_NAME } from 'data/fields/hooks/account-fields';
import { ACCOUNT_CONFIG_QUERY_KEY } from 'queries/account';
import { getProjectDependencyMapKey } from 'queries/common';
import { logger } from 'services';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import {
  CreateMultipleSubTasksActionPayload,
  createMultipleSubTasksSagaAction,
} from '../actions';
import { addMultipleSubTasksToTask, createMultipleTaskSuccess } from '../slice';

function* createMultipleSubTasksSaga(
  action: StoreActionProps<CreateMultipleSubTasksActionPayload>,
): Generator {
  const { payload } = action;

  const { projectId, queryClient, ...rest } = payload;

  const accountConfig = queryClient.getQueryData<AccountConfig>(
    ACCOUNT_CONFIG_QUERY_KEY,
  );
  const statusField = accountConfig?.fields?.find(
    (f) =>
      f.fieldName === STATUS_FIELD_NAME &&
      f.objectType === FieldSectionType.Task,
  ) as Field | undefined;

  if (!statusField) {
    return;
  }

  const { fieldId } = statusField;
  const fieldValue = statusField.metaData?.default as number;

  try {
    yield put(
      addMultipleSubTasksToTask({
        projectId,
        parentTask: rest.parentTask,
        subtasks: rest.subtasks,
        fields: [{ fieldId, fieldValue }],
      }),
    );
    const tasks = (yield TaskAPI.addMultipleSubtasks(
      rest.parentTask,
      rest.subtasks.map((e) => ({ taskName: e.taskName })),
    )) as Task[];

    yield queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));

    yield put(
      createMultipleTaskSuccess({
        projectId,
        subtasks: rest.subtasks,
        tasks,
      }),
    );
    yield invalidateTaskQuery(rest.parentTask);
  } catch (error) {
    logger.error('createSubTaskSaga failed', { error });
  }
}

export function* watchCreateMultipleSubTaskSaga(): Generator {
  yield takeLatest(
    createMultipleSubTasksSagaAction.type,
    createMultipleSubTasksSaga,
  );
}
